/**
 * Seed actionState.diffs from the records that just got imported.
 *
 * After the Configuration AP eagerly executes an upload, every scope
 * already holds the freshly-imported rows. To show them in the
 * Datenbank tab as a diff view (all cells marked "new, pending"), we
 * list records per scope and synthesize one DiffRecord per row with
 * dbValue=null + fileValue=<imported> + accepted=null.
 *
 * PivotTable + DiffCell + DiffToolbar then handle rendering + per-row
 * accept / "Alle akzeptieren" / progress calculations without any
 * extra glue — we're reusing the same diff machinery the legacy
 * ingestion wizard drove. Per-cell rejection is purely visual in v1:
 * Anwenden finalizes the session regardless, and Verwerfen rolls it
 * back as a whole.
 */

import { api } from "../../../api/client"
import { useStore } from "../../../store/useStore"
import type {
  DiffCell,
  DiffRecord,
  DiffState,
  PrimitiveType,
  ScopeDiffState,
  ScopeListItem,
} from "../../../types"

export async function seedImportDiff(fileName: string): Promise<void> {
  const store = useStore.getState()
  const scopes = store.scopes
  if (scopes.length === 0) return

  // Order scopes by display_order so the primary tab is intuitive.
  const ordered = [...scopes].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
  )
  let first = true

  for (const scopeItem of ordered) {
    const scopeDiff = await buildScopeDiff(scopeItem, fileName)
    if (!scopeDiff) continue

    if (first) {
      // First scope initializes actionState; subsequent ones append.
      useStore.getState().enterDiffMode({
        scopeId: scopeDiff.scopeId,
        fileName: scopeDiff.fileName,
        records: scopeDiff.records,
        rawExtractions: scopeDiff.rawExtractions,
        anchoredRecords: scopeDiff.anchoredRecords,
      } as DiffState)
      first = false
    } else {
      useStore.getState().setActionScopeDiff(scopeItem.id, scopeDiff)
    }
  }
}

async function buildScopeDiff(
  scopeItem: ScopeListItem,
  fileName: string,
): Promise<ScopeDiffState | null> {
  let page
  try {
    page = await api.listRecords(scopeItem.id)
  } catch {
    return null
  }
  const fullScope = page.scope
  if (!fullScope || page.records.length === 0) return null

  const pkPrim = fullScope.primitives.find((p) => p.is_pk)
  const namePrim = fullScope.primitives.find(
    (p) => !p.is_pk && p.type === "string" && p.is_visible,
  )

  const diffRecords: Record<string, DiffRecord> = {}
  for (const rec of page.records) {
    const cells: Record<string, DiffCell> = {}
    for (const prim of fullScope.primitives) {
      // Relations + formulas are surfaced by their own diff channels /
      // computed on the fly — skip them here to avoid fabricating bogus
      // cell-level diffs.
      if (prim.type === "relation" || prim.type === "formula") continue
      const pv = rec.values[prim.name]
      const raw = pv?.value ?? null
      if (raw === null || raw === "") continue
      cells[prim.id] = {
        recordId: rec.id,
        primitiveId: prim.id,
        primitiveName: prim.name,
        primitiveTitle: prim.title,
        primitiveType: prim.type as PrimitiveType,
        dbValue: null,
        fileValue: raw,
        rawFileValue: raw,
        typeIncompatible: false,
        accepted: null,
      }
    }
    diffRecords[rec.id] = {
      recordId: rec.id,
      pkValue: pkPrim ? (rec.values[pkPrim.name]?.value ?? rec.id) : rec.id,
      nameValue: namePrim ? (rec.values[namePrim.name]?.value ?? null) : null,
      isNew: true,
      cells,
    }
  }

  if (Object.keys(diffRecords).length === 0) return null

  return {
    scopeId: scopeItem.id,
    fileName,
    records: diffRecords,
    rawExtractions: [],
    anchoredRecords: [],
  }
}
